import { useParams, useSearchParams } from "react-router-dom";
import { Button, DateInput, Input } from "../../../components/ui";
import BreadCrumbs from "../../../components/ui/BreadCrumbs";
import useAddEditUserSalary from "../../../hooks/salary/useAddEditUserSalary";
import AddComponentRowModal from "../../../components/salaryTemplates/AddComponentRowModal";
import { TrashIcon } from "@heroicons/react/solid";
import Checkbox from "../../../components/ui/Checkbox";
import { checkIfNotEmpty } from "../../../utility";

const AddUserSalaryDetails = () => {
  const { id, username } = useParams();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source");
  const employeeType = searchParams.get("employeeType");

  const breadcrumbs = [
    {
      label: "Salary Details",
      link: `/employee-salary-details/${id}/${username}?source=${source}`,
    },
    {
      label: "Select Salary Template",
      link: `/employee-salary/${id}/${username}?employeeType=${
        employeeType ? employeeType : ""
      }&source=${source}`,
    },
    {
      label: "Add Salary Details",
    },
  ];

  const {
    templateName,
    toggleAddRowModal,
    showAddRowModal,
    control,
    watch,
    handleSubmit,
    othersMonthlyValue,
    othersAnnualValue,
    handleCTCBlur,
    addRows,
    calculateAnnualOnMonthlyChange,
    calculateMonthlyOnAnnualChange,
    removeRow,
    statutoryComponentsList,
    isFetchingStatutory,
    // For saving
    isSaving,
    callHandleSave,
  } = useAddEditUserSalary({
    templateId: searchParams.get("templateId"),
    employeeId: id,
    username: username,
    source: source,
  });

  const earningsArr = watch("earnings") || [];

  return (
    <div>
      <div className="p-4 flex justify-between sticky bg-white top-16 shadow-lg">
        <h2 className="font-semibold text-2xl">
          Add Employee Salary Details for "{username}"
        </h2>

        <Button onClick={handleSubmit(callHandleSave)}>Save Salary Details</Button>
      </div>
      <div className="px-4 my-5">
        <BreadCrumbs crumbs={breadcrumbs} />
      </div>
      <div className="p-4">
        {showAddRowModal && (
          <AddComponentRowModal
            isOpen={showAddRowModal}
            toggleModal={toggleAddRowModal}
            addRows={addRows}
            addedRows={earningsArr}
          />
        )}

        {checkIfNotEmpty(templateName) && (
          <p className="mb-5 text-sm">
            <span className="font-semibold">Chosen template: </span>"
            {templateName}"
          </p>
        )}

        <div className="flex items-center justify-between mb-5">
          <DateInput
            label="Effect From"
            control={control}
            name="from_date"
            className="w-64"
            rules={{ required: "Please select effect from date" }}
          />

          <Input
            label="Annual CTC"
            control={control}
            name="annual_ctc"
            type="number"
            isPositiveNumber={true}
            isCurrency={true}
            className="w-64"
            onBlurFn={handleCTCBlur}
            rules={{ required: "Please fill in annual CTC" }}
          />
        </div>

        <div className="flex items-center justify-between mb-5">
          <p className="font-medium">Earnings</p>

          <Button onClick={toggleAddRowModal}>Add row</Button>
        </div>

        <div className="border p-5 border-gray-300 rounded-md grid grid-cols-7 items-center">
          <p className="font-medium col-span-2">Salary components</p>
          <p className="font-medium col-span-2">Monthly amount</p>
          <p className="font-medium col-span-2">Annual amount</p>
          <p></p>

          {/* {earningsArr?.length === 0 && (
            <p className="text-gray-400 pt-16 pb-10 col-span-7 text-center">
              No earnings components added
            </p>
          )} */}

          {earningsArr?.map((el, idx) => {
            return (
              <>
                <p className="pt-4 col-span-2">{el.name}</p>
                <p className="pt-4 col-span-2">
                  <Input
                    type="number"
                    isPositiveNumber={true}
                    control={control}
                    className="border p-1 rounded mr-5"
                    parentClassName="mr-5"
                    blockUntilOnChangeIsTrue={true}
                    onChangeFn={(e) => calculateAnnualOnMonthlyChange(e, idx)}
                    disabled={!el.is_monthly_editable}
                    name={`earnings[${idx}].monthly_value`}
                  />
                </p>
                <p className="pt-4 col-span-2">
                  <Input
                    type="number"
                    isPositiveNumber={true}
                    control={control}
                    className="border p-1 rounded"
                    blockUntilOnChangeIsTrue={true}
                    onChangeFn={(e) => calculateMonthlyOnAnnualChange(e, idx)}
                    disabled={!el.is_yearly_editable}
                    name={`earnings[${idx}].annual_value`}
                  />
                </p>
                <div className="pt-4 grid place-items-center h-full">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeRow(idx)}
                  >
                    <TrashIcon className="w-6 h-6 text-red-500" />
                  </Button>
                </div>
              </>
            );
          })}

          <p className="pt-4 col-span-2">Others</p>
          <p className="pt-4 col-span-2">&#8377; {othersMonthlyValue}</p>
          <p className="pt-4 col-span-2">&#8377; {othersAnnualValue}</p>
          <p className="pt-4"></p>
        </div>

        <p className="font-semibold mt-5 mb-4">Statutory components:</p>

        {isFetchingStatutory && (
          <p className="text-gray-500 py-10">Loading components...</p>
        )}

        {statutoryComponentsList.map((sc, idx) => {
          return (
            <div key={idx} className="mb-4">
              <div className="flex items-center mb-1.5">
                <Checkbox
                  label={sc.name}
                  control={control}
                  name={`statutory.${sc.name}`}
                />

                {sc.manage_components && (
                  <Button variant="outline" className={"ml-3 !py-1"}>
                    Manage components
                  </Button>
                )}
              </div>
              <p className="ml-5 text-sm text-gray-500">{sc.note}</p>
            </div>
          );
        })}

        <div className="flex justify-end">
          <Button onClick={handleSubmit(callHandleSave)} className="mt-5">
            Save Salary Details
          </Button>
        </div>
        {isSaving && <p className="text-right w-full py-4">Saving...</p>}
      </div>
    </div>
  );
};

export default AddUserSalaryDetails;
